import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { 
  Phone, MessageCircle, ArrowRight, Filter, ZoomIn, Images
} from 'lucide-react';
import { ImageGallerySlider } from '../components/ui/ImageGallerySlider';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  'All',
  'Front Elevation',
  'Office Partitions',
  'Home Glass Work',
  'Shower Enclosures',
  'Balcony and Stair Glass Work',
  'Sliding Windows and Doors',
  'Front Elevation Facade Work',
];

export const Gallery = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [galleryModal, setGalleryModal] = useState({ open: false, project: null });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        setFilteredProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, projects]);

  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I saw your project gallery and I am interested in similar work.')}`;

  const openGallery = (project) => {
    // Combine main image, before/after images, and additional images
    const allImages = [
      project.image,
      project.before_image,
      project.after_image,
      ...(project.images || [])
    ].filter(Boolean);
    setGalleryModal({ open: true, project: { ...project, allImages } });
  };

  const closeGallery = () => {
    setGalleryModal({ open: false, project: null });
  };

  return (
    <div data-testid="gallery-page">
      <SEOHead {...SEO_CONFIG.gallery} />
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1764296377290-51123bc0cbce?w=1920&q=80')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-orange-400 font-semibold mb-4">OUR WORK</p>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Project Gallery
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Explore our portfolio of completed projects — from stunning ACP facades to elegant glass partitions. 
              Every project showcases our commitment to quality and craftsmanship.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="gallery-quote-btn">
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-white" data-testid="featured-projects">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">FEATURED WORK</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Highlight Projects
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* ACP HPL Front Elevation */}
            <div className="relative group rounded-2xl overflow-hidden aspect-[4/3]">
              <img 
                src="https://images.unsplash.com/photo-1764296377290-51123bc0cbce?w=800&q=80"
                alt="ACP HPL Front Elevation Work"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-80"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full mb-3 inline-block">
                  Featured Project
                </span>
                <h3 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-white mb-2">
                  ACP HPL Front Elevation Work
                </h3>
                <p className="text-slate-300 mb-4">
                  Complete ACP and HPL facade installation for commercial buildings with modern design and durability.
                </p>
                <Link to="/contact" state={{ service: 'ACP Front Elevation' }}>
                  <Button className="bg-white text-slate-900 hover:bg-slate-100">
                    Get Similar Work
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* LED Panels & PVC Louvers */}
            <div className="relative group rounded-2xl overflow-hidden aspect-[4/3]">
              <img 
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
                alt="LED Panels and PVC Louvers"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-80"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="px-3 py-1 bg-sky-500 text-white text-xs font-medium rounded-full mb-3 inline-block">
                  Featured Project
                </span>
                <h3 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-white mb-2">
                  LED Panels / PVC Louvers & Charcoal Work
                </h3>
                <p className="text-slate-300 mb-4">
                  Modern facade design combining LED panels, PVC louvers, and charcoal panels for stunning exteriors.
                </p>
                <Link to="/contact" state={{ service: 'PVC Louvers' }}>
                  <Button className="bg-white text-slate-900 hover:bg-slate-100">
                    Get Similar Work
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-slate-50 border-y sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-slate-500 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`gallery-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'bg-sky-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="gallery-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] skeleton rounded-xl"></div>
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No projects found in this category.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setSelectedCategory('All')}
              >
                View All Projects
              </Button>
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredProjects.map((project) => {
                const imageCount = [project.image, project.before_image, project.after_image, ...(project.images || [])].filter(Boolean).length;
                return (
                  <div 
                    key={project.id}
                    className="gallery-item group cursor-pointer"
                    onClick={() => openGallery(project)}
                    data-testid={`gallery-item-${project.id}`}
                  >
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="gallery-overlay">
                      <div className="w-full">
                        <span className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full mb-2 inline-block">
                          {project.category}
                        </span>
                        <h3 className="font-['Oswald'] text-lg font-semibold text-white">{project.title}</h3>
                        <p className="text-slate-300 text-sm mt-1 line-clamp-2">{project.description}</p>
                      </div>
                    </div>
                    {/* Image count badge */}
                    {imageCount > 1 && (
                      <div className="absolute top-4 left-4">
                        <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                          <Images className="w-3 h-3" />
                          {imageCount}
                        </span>
                      </div>
                    )}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                        <ZoomIn className="w-5 h-5 text-slate-700" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Image Gallery Modal */}
      <Dialog open={galleryModal.open} onOpenChange={(open) => !open && closeGallery()}>
        <DialogContent className="max-w-5xl p-4 bg-white">
          <h3 className="text-xl font-bold text-slate-900 mb-4">{galleryModal.project?.title}</h3>
          {galleryModal.project && (
            <ImageGallerySlider
              images={galleryModal.project.allImages?.map(url => ({ url, thumbnail: url })) || []}
              showThumbnails={true}
              showArrows={true}
              showDots={true}
              enableZoom={true}
              enableFullscreen={true}
              aspectRatio="16/9"
              autoPlay={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-sky-700" data-testid="gallery-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Like What You See?
          </h2>
          <p className="text-sky-100 text-lg mb-8">
            Let us create something beautiful for your space. Contact us to discuss your project.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:08512853955">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50">
                <Phone className="w-5 h-5 mr-2" />
                Call: 085128 53955
              </Button>
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25d366] hover:bg-[#20bd5a] text-white">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
